import type { CoverageReport, ReportingOverview } from "../../api/types.js";
import {
  CERTIFICATION_STATUS,
  catalogItem,
  type ReportCatalogItem,
  type ReportFormatId,
} from "./reportCatalog.js";

export interface ReportMetric {
  id: string;
  label: string;
  value: number | null;
  limitation: string | null;
}

export type EvidenceSlotState = "observed" | "not_assessed";

export interface EvidenceSlot {
  id: string;
  label: string;
  state: EvidenceSlotState;
  observation: string;
}

export interface ReportEvidence {
  tenantId: string;
  scopeLabel: string;
  generatedAt: string;
  scanId: string | null;
  revision: string | null;
  loading: boolean;
  metrics: readonly ReportMetric[];
  funnel: readonly { id: string; label: string; value: number }[];
  reconciliation: readonly ReportMetric[];
  coverage: readonly ReportMetric[];
  secrets: readonly ReportMetric[];
  scanDelta: readonly ReportMetric[];
  slots: readonly EvidenceSlot[];
  limitations: readonly string[];
  pqc: {
    available: boolean;
    complete: boolean;
    total: number | null;
    hashingObservationCount: number | null;
    classifications: readonly { value: string; count: number; findingLinkedCount: number }[];
    limitation: string;
  };
  sbom: {
    available: boolean;
    complete: boolean;
    total: number | null;
    uniqueComponents: number | null;
    limitation: string;
  };
  unavailable: boolean;
}

export interface ReportDossier {
  format: ReportCatalogItem;
  evidence: ReportEvidence;
  generatedAt: string;
  certificationStatus: typeof CERTIFICATION_STATUS;
  stamp: string;
}

function metric(
  id: string,
  label: string,
  value: number | null,
  limitation: string | null = null,
): ReportMetric {
  return { id, label, value, limitation };
}

function overviewMetric(overview: ReportingOverview, id: string): ReportMetric | null {
  const found = overview.metrics.find((item) => item.id === id);
  if (!found) return null;
  return metric(found.id, found.label, found.value, found.limitation);
}

export function buildEvidenceSlots(
  overview: ReportingOverview | null,
  coverage: CoverageReport | null,
): EvidenceSlot[] {
  if (!overview) {
    return [
      {
        id: "reporting_contract",
        label: "Reporting contract",
        state: "not_assessed",
        observation: "No verified overview payload was returned for this scope.",
      },
    ];
  }
  const actionable =
    overviewMetric(overview, "actionable") ?? overviewMetric(overview, "actionable-findings");
  const needsHuman = overviewMetric(overview, "needs-human");
  const replay = overviewMetric(overview, "replay-failures");
  const coverageGaps = overviewMetric(overview, "coverage-gaps");
  const currentSecrets = overviewMetric(overview, "current-secrets");
  const coverageItem = coverage?.items[0] ?? null;
  return [
    {
      id: "finding_queue",
      label: "Finding decision queue",
      state: "observed",
      observation: `${formatMetricValue(actionable?.value ?? null)} actionable · ${formatMetricValue(needsHuman?.value ?? null)} waiting on human review`,
    },
    {
      id: "replay",
      label: "Evidence replay",
      state: "observed",
      observation: `${formatMetricValue(replay?.value ?? null)} replay failures. Citations that did not replay stay visible.`,
    },
    {
      id: "coverage",
      label: "Coverage honesty",
      state: "observed",
      observation: coverageItem
        ? `${coverageItem.label}: ${coverageItem.covered}/${coverageItem.total}. ${formatMetricValue(coverageGaps?.value ?? null)} gap cells.`
        : `${formatMetricValue(coverageGaps?.value ?? null)} coverage gap cells. Empty is not reviewed.`,
    },
    {
      id: "sbom",
      label: "SBOM inventory",
      state: overview.sbomInventory.available ? "observed" : "not_assessed",
      observation: overview.sbomInventory.available
        ? `${formatMetricValue(overview.sbomInventory.uniqueComponents ?? overview.sbomInventory.total)} unique components. ${overview.sbomInventory.limitation}`
        : overview.sbomInventory.limitation || "SBOM inventory was not returned.",
    },
    {
      id: "pqc",
      label: "Cryptography inventory",
      state: overview.pqcInventory.available ? "observed" : "not_assessed",
      observation: overview.pqcInventory.available
        ? `${formatMetricValue(overview.pqcInventory.total)} observed uses. Ordinary hashing is not a finding. Retention horizon unknown from static source.`
        : overview.pqcInventory.limitation || "PQC inventory was not returned.",
    },
    {
      id: "secrets",
      label: "Secret posture",
      state: "observed",
      observation: `${formatMetricValue(currentSecrets?.value ?? overview.secretPosture.currentCount)} current-source secrets. ${overview.secretPosture.limitation}`,
    },
    {
      id: "access_review",
      label: "Access review / identity governance",
      state: "not_assessed",
      observation: "This scan does not assess account provisioning, reviews, or revocation.",
    },
    {
      id: "incident_readiness",
      label: "Incident response program",
      state: "not_assessed",
      observation: "No incident-response control assessment is in this product.",
    },
  ];
}

export function evidenceFromContracts(input: {
  overview: ReportingOverview | null;
  coverage?: CoverageReport | null;
  scanId?: string | null;
  loading?: boolean;
}): ReportEvidence {
  const { overview, coverage = null, scanId = null, loading = false } = input;
  if (!overview) {
    return {
      tenantId: loading ? "loading" : "unavailable",
      scopeLabel: scanId
        ? `Scan ${scanId}`
        : loading
          ? "Loading reporting contract"
          : "No reporting contract answered",
      generatedAt: new Date().toISOString(),
      scanId,
      revision: null,
      loading,
      metrics: [],
      funnel: [],
      reconciliation: [],
      coverage: [],
      secrets: [],
      scanDelta: [],
      slots: loading ? [] : buildEvidenceSlots(null, null),
      limitations: loading
        ? ["Reporting is still loading. Unavailable values will not be replaced with zero."]
        : [
            "Verified reporting is unavailable. No totals are invented in place of a missing contract.",
          ],
      pqc: {
        available: false,
        complete: false,
        total: null,
        hashingObservationCount: null,
        classifications: [],
        limitation: "PQC inventory was not returned.",
      },
      sbom: {
        available: false,
        complete: false,
        total: null,
        uniqueComponents: null,
        limitation: "SBOM inventory was not returned.",
      },
      unavailable: !loading,
    };
  }
  const recon = overview.projection.reconciliation;
  return {
    tenantId: overview.tenantId,
    scopeLabel: overview.scopeLabel,
    generatedAt: overview.generatedAt,
    scanId: overview.projection.scope.scanId ?? scanId,
    revision: overview.revision,
    loading: false,
    metrics: overview.metrics.map((item) =>
      metric(item.id, item.label, item.value, item.limitation),
    ),
    funnel: overview.funnel.map((stage) => ({
      id: stage.id,
      label: stage.label,
      value: stage.value,
    })),
    reconciliation: [
      metric("raw", "Raw candidates", recon.rawCandidates),
      metric("replay_matched", "Replay-matched", recon.replayMatchedCandidates),
      metric("confirmed", "Confirmed", recon.confirmed),
      metric("needs_human", "Needs human", recon.needsHuman),
      metric("challenged", "Challenged or refuted", recon.challengedOrRefuted),
      metric("verified_fixes", "Verified fixes", recon.verifiedFixes),
    ],
    coverage: (coverage?.items ?? []).map((item) =>
      metric(
        item.id,
        item.label,
        item.total === 0 ? null : item.covered,
        `${item.covered} of ${item.total} ${item.unitLabel}`,
      ),
    ),
    secrets: [
      metric(
        "current_secrets",
        "Current-source secrets",
        overview.secretPosture.currentCount,
        overview.secretPosture.limitation,
      ),
      metric(
        "history_only_secrets",
        "History-only secrets",
        overview.secretPosture.historyOnlyCount,
        overview.secretPosture.historyCoverageComplete ? null : "History coverage is incomplete.",
      ),
    ],
    scanDelta: overview.scanDelta.available
      ? [
          metric("delta_new", "New", overview.scanDelta.new),
          metric("delta_persistent", "Persistent", overview.scanDelta.persistent),
          metric("delta_fixed", "Fixed", overview.scanDelta.fixed),
          metric("delta_reopened", "Reopened", overview.scanDelta.reopened),
        ]
      : [metric("delta", "Same-repository delta", null, overview.scanDelta.limitation)],
    slots: buildEvidenceSlots(overview, coverage),
    limitations: [
      ...overview.limitations,
      ...(coverage?.limitations ?? []),
      overview.sbomInventory.limitation,
      overview.pqcInventory.limitation,
    ].filter(Boolean),
    pqc: {
      available: overview.pqcInventory.available,
      complete: overview.pqcInventory.complete,
      total: overview.pqcInventory.total,
      hashingObservationCount: overview.pqcInventory.hashingObservationCount,
      classifications: overview.pqcInventory.classifications.map((row) => ({
        value: row.value,
        count: row.count,
        findingLinkedCount: row.findingLinkedCount,
      })),
      limitation: overview.pqcInventory.limitation,
    },
    sbom: {
      available: overview.sbomInventory.available,
      complete: overview.sbomInventory.complete,
      total: overview.sbomInventory.total,
      uniqueComponents: overview.sbomInventory.uniqueComponents,
      limitation: overview.sbomInventory.limitation,
    },
    unavailable: false,
  };
}

/** @deprecated Prefer evidenceFromContracts. Kept for call sites that only have overview. */
export function evidenceFromOverview(
  overview: ReportingOverview | null,
  scanId?: string | null,
): ReportEvidence {
  return evidenceFromContracts({ overview, scanId: scanId ?? null, loading: false });
}

export function buildDossier(
  formatId: ReportFormatId,
  evidence: ReportEvidence,
  generatedAt: string,
): ReportDossier {
  return {
    format: catalogItem(formatId),
    evidence,
    generatedAt,
    certificationStatus: CERTIFICATION_STATUS,
    stamp:
      formatId === "pqc_inventory"
        ? "Not a quantum-safe determination. Not a legal opinion."
        : "Not a compliance determination. No controls have been assessed.",
  };
}

export function formatMetricValue(value: number | null): string {
  return value === null ? "Unavailable" : String(value);
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function rowsHtml(
  items: readonly {
    id?: string;
    label?: string;
    value?: number | null;
    limitation?: string | null;
  }[],
  empty: string,
): string {
  if (items.length === 0) return `<tr><td colspan="3">${escapeHtml(empty)}</td></tr>`;
  return items
    .map((item) => {
      const label = "label" in item && item.label ? item.label : String(item.id ?? "");
      const value = "value" in item ? formatMetricValue(item.value ?? null) : "";
      const limitation = item.limitation ?? "—";
      return `<tr><th>${escapeHtml(label)}</th><td>${escapeHtml(value)}</td><td>${escapeHtml(limitation)}</td></tr>`;
    })
    .join("");
}

export function buildDossierHtml(dossier: ReportDossier): string {
  const { format, evidence } = dossier;
  const limitations = [...new Set([...evidence.limitations, dossier.stamp])]
    .map((item) => `<li>${escapeHtml(item)}</li>`)
    .join("");
  const slotRows = evidence.slots
    .map(
      (slot) =>
        `<tr><th>${escapeHtml(slot.label)}</th><td>${slot.state === "observed" ? "Observed" : "Not assessed"}</td><td>${escapeHtml(slot.observation)}</td></tr>`,
    )
    .join("");
  const pqcClass =
    format.id === "pqc_inventory"
      ? evidence.pqc.classifications
          .map(
            (row) =>
              `<tr><th>${escapeHtml(row.value)}</th><td>${row.count}</td><td>${row.findingLinkedCount} finding-linked</td></tr>`,
          )
          .join("") || `<tr><td colspan="3">No algorithm-class rows were returned.</td></tr>`
      : "";
  const inventory =
    format.id === "pqc_inventory"
      ? `<section>
  <h2>Observed cryptography inventory</h2>
  <p>Ordinary hashing is not treated as a finding. Retention horizon is unknown from static source.</p>
  <table>
    <tr><th>Inventory available</th><td>${evidence.pqc.available ? "Yes" : "No"}</td></tr>
    <tr><th>Inventory complete</th><td>${evidence.pqc.complete ? "Yes" : "No"}</td></tr>
    <tr><th>Observed uses</th><td>${escapeHtml(formatMetricValue(evidence.pqc.total))}</td></tr>
    <tr><th>Ordinary hashing observations</th><td>${escapeHtml(formatMetricValue(evidence.pqc.hashingObservationCount))}</td></tr>
  </table>
  <table><thead><tr><th>Class</th><th>Count</th><th>Finding-linked</th></tr></thead><tbody>${pqcClass}</tbody></table>
  <p>${escapeHtml(evidence.pqc.limitation)}</p>
</section>`
      : `<section>
  <h2>Supply-chain evidence offered to an assessor</h2>
  <table>
    <tr><th>SBOM available</th><td>${evidence.sbom.available ? "Yes" : "No"}</td></tr>
    <tr><th>SBOM complete</th><td>${evidence.sbom.complete ? "Yes" : "No"}</td></tr>
    <tr><th>SBOM components</th><td>${escapeHtml(formatMetricValue(evidence.sbom.uniqueComponents ?? evidence.sbom.total))}</td></tr>
    <tr><th>PQC inventory uses</th><td>${escapeHtml(formatMetricValue(evidence.pqc.total))}</td></tr>
  </table>
  <p>${escapeHtml(evidence.sbom.limitation)}</p>
</section>`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(format.name)} evidence folio</title>
  <style>
    @page { size: A4; margin: 14mm; }
    body { margin: 0; background: #ecebe7; color: #1a1a1a; font: 12px/1.45 "Iowan Old Style", Palatino, serif; }
    .folio { width: 210mm; min-height: 297mm; margin: 12px auto; padding: 16mm 16mm 18mm; background: #fff; box-sizing: border-box; }
    header { display: flex; justify-content: space-between; gap: 16px; border-bottom: 2px solid #0b1f4a; padding-bottom: 10px; }
    h1 { margin: 0; color: #0b1f4a; font: 560 22px/1.1 "Avenir Next", system-ui, sans-serif; letter-spacing: -0.03em; }
    .stamp { border: 1px solid #b8860b; color: #7a5b12; font: 700 11px/1.3 system-ui, sans-serif; padding: 6px 10px; text-transform: uppercase; letter-spacing: 0.06em; }
    h2 { color: #0b1f4a; font: 600 13px/1.2 system-ui, sans-serif; letter-spacing: 0.04em; text-transform: uppercase; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border-bottom: 1px solid #d0d4dc; padding: 6px 0; text-align: left; vertical-align: top; }
    th { width: 38%; color: #4a4a4a; font: 650 11px/1.3 system-ui, sans-serif; }
    footer { margin-top: 24px; color: #4a4a4a; font: 11px/1.4 system-ui, sans-serif; }
  </style>
</head>
<body>
  <article class="folio">
    <header>
      <div>
        <p>Provenance · evidence folio · ISO 216 A4</p>
        <h1>${escapeHtml(format.name)}</h1>
        <p>${escapeHtml(format.edition)}</p>
      </div>
      <p class="stamp">${escapeHtml(dossier.stamp)}</p>
    </header>
    <section>
      <h2>Scope</h2>
      <table>
        <tr><th>Tenant</th><td>${escapeHtml(evidence.tenantId)}</td></tr>
        <tr><th>Scope</th><td>${escapeHtml(evidence.scopeLabel)}</td></tr>
        <tr><th>Scan</th><td>${escapeHtml(evidence.scanId ?? "latest per repository")}</td></tr>
        <tr><th>Generated</th><td>${escapeHtml(dossier.generatedAt)}</td></tr>
        <tr><th>Certification status</th><td>${CERTIFICATION_STATUS}</td></tr>
      </table>
    </section>
    <section>
      <h2>Admitted metrics</h2>
      <table>
        <thead><tr><th>Metric</th><th>Value</th><th>Limitation</th></tr></thead>
        <tbody>${rowsHtml(evidence.metrics, "No verified metrics were returned for this scope.")}</tbody>
      </table>
    </section>
    <section>
      <h2>Decision funnel</h2>
      <table>
        <thead><tr><th>Stage</th><th>Count</th><th></th></tr></thead>
        <tbody>${rowsHtml(
          evidence.funnel.map((stage) => ({
            label: stage.label,
            value: stage.value,
            limitation: "—",
          })),
          "No funnel stages were returned.",
        )}</tbody>
      </table>
    </section>
    <section>
      <h2>Reconciliation</h2>
      <table>
        <thead><tr><th>Count</th><th>Value</th><th></th></tr></thead>
        <tbody>${rowsHtml(evidence.reconciliation, "No reconciliation counts were returned.")}</tbody>
      </table>
    </section>
    <section>
      <h2>Evidence offered to an assessor</h2>
      <table>
        <thead><tr><th>Slot</th><th>State</th><th>Observation</th></tr></thead>
        <tbody>${slotRows || `<tr><td colspan="3">No evidence slots were derived.</td></tr>`}</tbody>
      </table>
    </section>
    ${inventory}
    <section>
      <h2>Limitations</h2>
      <ul>${limitations}</ul>
    </section>
    <footer>
      Recreatable from this scan. Empty or unavailable values are not substituted with zero.
      ${escapeHtml(format.focus)}
    </footer>
  </article>
</body>
</html>`;
}

export function downloadTextFile(filename: string, mimeType: string, content: string): void {
  const link = document.createElement("a");
  let objectUrl: string | undefined;
  if (typeof URL.createObjectURL === "function") {
    objectUrl = URL.createObjectURL(new Blob([content], { type: mimeType }));
    link.href = objectUrl;
  } else {
    link.href = `data:${mimeType};charset=utf-8,${encodeURIComponent(content)}`;
  }
  link.download = filename;
  link.rel = "noopener";
  document.body.append(link);
  link.click();
  link.remove();
  if (objectUrl) URL.revokeObjectURL(objectUrl);
}
